import type { ObjectLike } from "@core/domain/types/object-like.type.ts";
import { get, set } from "es-toolkit/compat";

export class FormDataSerializer {
    private static appendFormData(
        formData: FormData,
        key: string,
        value: unknown,
    ): void {
        if (value === undefined) {
            return;
        }

        if (value instanceof Date) {
            formData.append(key, value.toISOString());
        } else if (value === null) {
            formData.append(key, ""); // If null, send empty string
        } else if (typeof value === "boolean") {
            formData.append(key, value ? "1" : "0");
        } else if (value instanceof File) {
            formData.append(key, value);
        } else if (Array.isArray(value)) {
            if (value.length === 0) {
                FormDataSerializer.appendFormData(formData, `${key}[]`, "");
            }
            value.forEach((element) => {
                // const elementKey =
                //     element instanceof Object
                //         ? `${key}[${index}]`
                //         : `${key}[]`;
                //
                // !!! We force to send second array index empty because API needs it
                const elementKey = `${key}[]`;
                FormDataSerializer.appendFormData(
                    formData,
                    elementKey,
                    element,
                );
            });
        } else if (typeof value === "object") {
            for (const [subKey, subValue] of Object.entries(value)) {
                FormDataSerializer.appendFormData(
                    formData,
                    `${key}[${subKey}]`,
                    subValue,
                );
            }
        } else if (typeof value === "string") {
            formData.append(key, value);
        } else if (typeof value === "number" || typeof value === "bigint") {
            formData.append(key, value.toString());
        }
    }

    static serialize<T extends ObjectLike<T>>(data: T): FormData {
        const formData = new FormData();

        for (const key in data) {
            if (Object.hasOwn(data, key)) {
                FormDataSerializer.appendFormData(formData, key, data[key]);
            }
        }

        return formData;
    }

    static unserialize<T extends ObjectLike<T>>(formData: FormData): T {
        const data: T = {} as T;

        formData.forEach((value, key) => {
            const keys = key.split(/\[|\]/).filter((k) => k);
            keys.reduce<unknown>((acc, k, i) => {
                if (typeof acc !== "object" || acc === null) return acc;

                if (i === keys.length - 1) {
                    set(acc, k, value);
                } else if (!get(acc, k)) {
                    set(acc, k, isNaN(Number(keys[i + 1])) ? {} : []);
                }

                return get(acc, k);
            }, data);
        });

        return data;
    }
}
